import { Component, OnDestroy, OnInit } from "@angular/core";
import { NgxSpinnerService } from "ngx-spinner";
import { Subject, Subscription } from "rxjs";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";

import { AnalyzeReportOperatorModel } from "src/app/models/analyze-report-operator-model";
import {
  STORAGE_KEY_CURRENT_USER,
  STORAGE_KEY_ID_PERFIL,
} from "src/app/storageCore/constStorageKeys";
import storageX from "src/app/storageCore/storageX";

import { MatTableDataSource } from "@angular/material/table";
import { PageEvent } from "@angular/material/paginator";
import { AnalyzeReportOperatorModelPaginated } from "src/app/models/analyze-report-operator-model-paginated";
import { Router } from "@angular/router";
import { DatePipe } from "@angular/common";

import Swal from "sweetalert2";
import { AnalyzeTripService } from "src/app/services/analyze-trip.service";
import { MinioService } from "src/app/services/minio.service";
import { AnalyzeReportOperatorWsService } from "src/app/services/analyze-report-operator-ws.service";

@Component({
  selector: "app-analyze-report-operator",
  templateUrl: "./analyze-report-operator.component.html",
  styleUrls: ["./analyze-report-operator.component.scss"],
})
export class AnalyzeReportOperatorComponent implements OnInit, OnDestroy {
  public analyzeTripService: AnalyzeTripService;
  public minioService: MinioService;

  public datePipe: DatePipe;
  public actualPage: PageEvent;

  public analyzeReportOperatorModelsPaginated: AnalyzeReportOperatorModelPaginated;
  public analyzeReportOperatorModel: AnalyzeReportOperatorModel;
  public analyzeReportOperatorsPage: AnalyzeReportOperatorModelPaginated;

  public analyzesOperators: MatTableDataSource<any> =
    new MatTableDataSource<any>();

  public tableColumns: string[];
  public typeFieldSelect = [
    { name: "Número Pacote", value: "PACKAGE" },
    { name: "Motorista", value: "DRIVER" },
    { name: "Placa", value: "PLATE" },
    { name: "Empresa", value: "COMPANY" },
  ];

  public permissions: [];
  private filterSearch$ = new Subject<string>();
  private filterSearchSubscription: Subscription;
  private wsSubscription: Subscription;
  private readonly filterDebounceMs = 400;

  public filter = "";
  public baseFilter = "";
  public field = "";
  public typeFieldFilterSelected;
  public errorMessage;
  public isLoadingInitial = true;

  constructor(
    datePipe: DatePipe,
    private spinner: NgxSpinnerService,
    public router: Router,
    analyzeTripService: AnalyzeTripService,
    minioService: MinioService,
    private analyzeReportOperatorWsService: AnalyzeReportOperatorWsService
  ) {
    this.datePipe = datePipe;
    this.analyzeTripService = analyzeTripService;
    this.minioService = minioService;
  }

  ngOnInit(): void {
    this.typeFieldFilterSelected = this.typeFieldSelect[0].value;
    this.field = this.typeFieldFilterSelected;
    this.initialPage();
    this.loadTableDefinitions();
    this.setupFilterDebounce();
    this.loadReports(
      this.actualPage,
      this.filter,
      this.field,
      this.baseFilter,
      true
    );
    this.setupWebSocketUpdates();
  }

  private setupWebSocketUpdates(): void {
    const profile: string = storageX.whereKeyIs(STORAGE_KEY_ID_PERFIL).get();
    this.analyzeReportOperatorWsService.connect(profile);

    this.wsSubscription = this.analyzeReportOperatorWsService.updates$.subscribe(
      () => {
        this.loadReports(
          this.actualPage,
          this.filter,
          this.field,
          this.baseFilter,
          false,
          false
        );
      }
    );
  }

  private setupFilterDebounce(): void {
    this.filterSearchSubscription = this.filterSearch$
      .pipe(debounceTime(this.filterDebounceMs), distinctUntilChanged())
      .subscribe(() => {
        this.actualPage.pageIndex = 0;
        this.loadReports(
          this.actualPage,
          this.filter,
          this.field,
          this.baseFilter,
          false,
          true
        );
      });
  }

  ngOnDestroy(): void {
    this.wsSubscription?.unsubscribe();
    this.analyzeReportOperatorWsService.disconnect();
    this.filterSearchSubscription?.unsubscribe();
    this.filterSearch$.complete();
    this.spinner.hide();
  }

  private loadReports(
    page: PageEvent,
    name: string,
    field: string,
    base: string,
    initialLoad = false,
    showSpinner = true
  ): void {
    if (initialLoad) {
      this.isLoadingInitial = true;
    }

    if (showSpinner) {
      this.spinner.show();
    }

    var profile: string = storageX.whereKeyIs(STORAGE_KEY_ID_PERFIL).get();
    this.analyzeTripService
      .getAllAnalyzeReportOperator(page, profile, name, field, base)
      .subscribe(
        (t) => {
          this.analyzeReportOperatorsPage = t;
          this.analyzesOperators =
            new MatTableDataSource<AnalyzeReportOperatorModel>(
              this.analyzeReportOperatorsPage.content
            );
          this.isLoadingInitial = false;
          if (showSpinner) {
            this.spinner.hide();
          }
        },
        (error) => {
          console.log("Erro ao obter as analises do operador");
          this.isLoadingInitial = false;
          if (showSpinner) {
            this.spinner.hide();
          }
        }
      );
  }

  openInNewTab(uuid: string) {
    const url = "/analise-operacional/view-tratamento-operador/" + uuid;
    window.open(url, "_blank");
  }

  openToTreatment(uuid: string) {
    const url = "/analise-operacional/treatment-select/" + uuid;
    window.open(url, "_blank");
  }

  openToUpdate(uuid: string) {
    const url = "/analise-operacional/edit/" + uuid;
    window.open(url, "_blank");
  }

  fieldFilter(): void {
    this.field = this.typeFieldFilterSelected;
    this.filter = "";
    this.applyFilter();
  }

  applyFilter(): void {
    this.filterSearch$.next(
      `${this.field}|${this.filter}|${this.baseFilter}`
    );
  }

  get filterFieldLabel(): string {
    const selectedField = this.typeFieldSelect.find(
      (type) => type.value === this.typeFieldFilterSelected
    );

    return selectedField ? selectedField.name.toLowerCase() : "campo";
  }

  private loadTableDefinitions(): void {
    this.tableColumns = [
      "packageNumber",
      "operationalBase",
      "dateStart",
      "dateEnd",
      "driverName",
      "plate",
      "statusReport",
      "state",
      "fileXlsxAnalytical",
      "filePdfAnalytical",
      "filePdfSynthetic",
      "operatorHandling",
      "viewOperatorHandling",
      "viewOperatorMonitoring",
      "sentManager",
      "finishReport",
      "update",
      "delete",
    ];
  }

  openMonitoringRequestPopup(uuid: string) {
    this.analyzeTripService.getByOperatorUuid(uuid).subscribe((resp: any) => {
      if (!resp) {
        Swal.fire({
          title: "Erro",
          text: "Não foi possível carregar os dados do monitoramento.",
          icon: "error",
          confirmButtonText: "Fechar",
        });
        return;
      }

      const solicitante = resp.name_applicant || "Não informado";

      const origemHtml = `
        <div style="margin-bottom: 15px;">
          <p><strong>Origem:</strong></p>
          <p><strong>Data Prevista de Origem:</strong> ${resp.expected_departure_date}</p>
          📍 <strong>${resp.addressname}</strong><br/>
          <strong>Latitude:</strong> ${resp.latitude}<br/>
          <strong>Longitude:</strong> ${resp.longitude}
        </div>
      `;

      let formattedAddresses = "Nenhum endereço de destino disponível.";
      if (
        resp.monitoring_destinations &&
        resp.monitoring_destinations.length > 0
      ) {
        formattedAddresses = resp.monitoring_destinations
          .map(
            (addr) => `
          <div style="margin-bottom: 15px;">
            📍 <strong>${addr.addressname}</strong><br/>
            <strong>Latitude:</strong> ${addr.latitude}<br/>
            <strong>Longitude:</strong> ${addr.longitude}
          </div>
        `
          )
          .join("");
      }

      Swal.fire({
        title: "Solicitação de Monitoramento",
        html: `
          <div style="
            text-align: left;
            font-size: 15px;
            max-height: 300px;
            overflow-y: auto;
          ">
            <p><strong>Solicitante do Monitoramento:</strong> ${solicitante}</p>
            <hr/>
            ${origemHtml}
            <hr/>
            <p><strong>Destinos:</strong></p>
            ${formattedAddresses}
          </div>
        `,
        icon: "info",
        confirmButtonText: "Fechar",
        confirmButtonColor: "#3085d6",
        width: "500px",
        customClass: {
          popup: "swal-monitoring-popup",
        },
      });
    });
  }

  public handleSentManager(element) {
    const url = "/analise-operacional/finalize-analysis/" + element;
    window.open(url, "_blank");
  }

  public async handleSentFinishReporth(element): Promise<void> {
    Swal.fire({
      icon: "warning",
      title: "Finalizar Relatório",
      html: `<h6>Tem certeza que você deseja finalizar o relatório? </h6>`,
      showCloseButton: false,
      showCancelButton: true,
      reverseButtons: true,
      cancelButtonText: "Cancelar",
      showConfirmButton: true,
      confirmButtonText: "Confirmar",
      focusConfirm: false,
    }).then((action) => {
      if (action.isConfirmed) {
        this.SentFinish(element);
      }
    });
  }

  public async handleDelete(element): Promise<void> {
    Swal.fire({
      icon: "warning",
      title: "Excluir Analise",
      html: `<h6>Tem certeza que você deseja excluir essa analise? </h6>`,
      showCloseButton: false,
      showCancelButton: true,
      reverseButtons: true,
      cancelButtonText: "Cancelar",
      showConfirmButton: true,
      confirmButtonText: "Confirmar",
      focusConfirm: false,
    }).then((action) => {
      if (action.isConfirmed) {
        this.deleteReports(element);
      }
    });
  }

  SentFinish(element): void {
    this.spinner.show();

    var operator = storageX.whereKeyIs(STORAGE_KEY_CURRENT_USER).get()?.id;
    this.analyzeTripService.finishReport(operator, element.uuid).subscribe(
      (v) => {
        console.log("Analise finalizada com sucesso");
        this.loadReports(
          this.actualPage,
          this.filter,
          this.field,
          this.baseFilter
        );
        this.spinner.hide();
      },
      (e) => {
        console.log("Erro ao finalizar analise");
        this.spinner.hide();
        this.errorMessage = e.message;
        throw new EvalError(e);
      }
    );
  }

  deleteReports(element): void {
    this.spinner.show();
    this.analyzeTripService.deleteAnalyzeReport(element.uuid).subscribe(
      (v) => {
        console.log("Exclusão realizada com sucesso");
        this.loadReports(
          this.actualPage,
          this.filter,
          this.field,
          this.baseFilter
        );
        this.spinner.hide();
      },
      (e) => {
        console.log("Erro ao excluir o transportador");
        this.spinner.hide();
        this.errorMessage = e.message;
        throw new EvalError(e);
      }
    );
  }

  exportReport(element): void {
    this.minioService.exportReport(element).subscribe();
  }

  onChangePage(event: PageEvent): void {
    this.actualPage = event;
    this.loadReports(
      this.actualPage,
      this.filter,
      this.field,
      this.baseFilter
    );
  }

  private initialPage(): void {
    this.actualPage = new PageEvent();
    this.actualPage.pageIndex = 0;
    this.actualPage.pageSize = 20;
  }

  getStatusStyle(status: string): { [key: string]: string } {
    switch (status) {
      case "ERROR":
        return { color: "red" };
      case "FINISHED":
        return { color: "green" };
      case "PROCESSING":
        return { color: "blue" };
      case "REPORT_EMPTY":
        return { color: "yellon" };
      case "PROCESSING_QUEUE":
        return { color: "orange" };
      default:
        return {};
    }
  }
}
